import { encrypt } from "@/services/CryptoService.js";

/**
 * The QR payload for a worksheet entry's label.
 *
 * Shared by the batch print page and by auto-print. A sticker printed at the
 * bench when the entry is saved and one reprinted from the batch page later have
 * to scan to the same thing, so there is exactly one place that decides what
 * goes into the code.
 *
 * LME products encode the human lot number itself; everything else encodes an
 * encrypted reference to the entry.
 */
export function buildQrValue({
  worksheet,
  entry,
  lotIndex,
  isLME,
  lmeBrand,
  companyName,
}) {
  if (isLME) {
    return `${lmeBrand || ""}|${worksheet.documentNumber}-${lotIndex}`;
  }
  return encrypt(
    JSON.stringify({
      owner: companyName,
      type: "DATA",
      businessType: "WorksheetEntry",
      worksheetId: worksheet.id,
      entryId: entry.id,
      timestamp: Date.now(),
    })
  );
}

/**
 * One sticker per logical entry: FIFO/LIFO splits arrive as several rows
 * sharing an entryGroupId but represent a single weighing, so they collapse
 * into one label with the weights summed.
 */
export function groupWorksheetEntries(entries) {
  if (!entries || entries.length === 0) {
    return [];
  }
  const groups = {};
  entries.forEach((entry) => {
    const groupId = entry.entryGroupId || entry.id;
    if (!groups[groupId]) {
      groups[groupId] = [];
    }
    groups[groupId].push(entry);
  });

  return Object.values(groups).map((groupEntries) => {
    if (groupEntries.length === 1) {
      return groupEntries[0];
    }
    const first = groupEntries[0];
    return {
      ...first,
      netWeight: groupEntries.reduce((sum, e) => sum + (e.netWeight || 0), 0),
      grossWeight: first.grossWeight,
      netAdjustmentWeight: groupEntries.reduce(
        (sum, e) => sum + (e.netAdjustmentWeight || 0),
        0
      ),
    };
  });
}

/**
 * The number printed on the label, as `documentNumber-<n>`. It is a POSITION,
 * not something stored on the entry, so it is only meaningful against the full
 * current entry list — and it changes if an earlier entry is deleted. Returns 0
 * when the entry is not in the list, which callers must treat as "do not print".
 */
export function lotIndexOf(entries, entry) {
  const grouped = groupWorksheetEntries(entries);
  const index = grouped.findIndex((e) => e.id == entry.id);
  return index >= 0 ? index + 1 : 0;
}
